import { useState } from "react";
import { IForm } from "../../";
import { lastSaveConflict } from "../../../../utils/lastSaveConflict";
import VehicleChildForm from "./components/vehicleChildForm";
import { requiredFieldsByStep } from "./utils/required-fields-by-step";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { fieldsLabels } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";

const ReleaseForm = (props: IForm) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = (props.occurrence?.occurrenceType === "P"
    ? requiredFieldsByStep.RELEASE_PREVENTIVE_CHECKUP
    : requiredFieldsByStep.RELEASE
  ).filter(f => props.occurrence?.occurrenceType !== 'D' || !['vehicleStopped', 'hasLink', 'mainOccurrence', 'tasNumber', 'tasStatus'].includes(f));

  const isDealership = props.occurrence?.occurrenceType === 'D';
  const serviceHiddenFields = isDealership
    ? ['estimatedTime','partOrderStatus','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','criticality','createdBy','dtStart','dtEnd','modules','estimateTimeRepair','vehicleStopped','hasLink','mainOccurrence','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload']
    : ['timeOpenProtocol','country','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload','modules','partOrderStatus'];

  return (
    <div>
      <div className="text-size-h4 font-bold">Liberação</div>
      <div className="text-color-status-resting text-size-14">
        Atribua os dados para relacionar a etapa de liberação
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "source",
          "hasLink",
          "mainOccurrence",
          "tasNumber",
          "tasStatus",
          "partOrderParts",
          "partOrderNumber",
          "partOrderStatusOrder",
          "partOrderDtEstimate",
          "partOrderDtDeliveryEstimate",
          "serviceBaySchedule",
          "dynamicsOccurrenceCode",
          "osNumber",
          "osDtOpenAt",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
                setHasError(!isValid);
                if (!isValid) {
                  setShowValidationErrorDialog(true);
                  return;
                }
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else if (!lastSaveConflict.current) {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              Salvar
            </button>
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao salvar"
        body="Ocorreu um erro ao salvar a etapa de liberação. Por favor, tente novamente ou entre em contato com o suporte."
      />

      <ErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title="Peça não preenchida"
        body="Há peças adicionadas sem preenchimento. Selecione a peça ou delete a linha para continuar."
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Liberação salva com sucesso"
        body={
          <div className="space-y-4">
            <div>A liberação foi salva com sucesso.</div>
            {hasError && (
              <>
                <div>
                  É necessário preencher todos os campos obrigatórios para
                  concluir a ocorrência.
                </div>
                <div>Campos obrigatórios não preenchidos:</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {fieldsLabels[field as keyof typeof fieldsLabels]}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

export default ReleaseForm;
